<template>
  <div class="mt-4" style="border: 1px solid #4c4b48">
    <v-row class="fill-height justify-center">
      <v-col>
        <v-form
          ref="form"
          lazy-validation
          @keydown.enter="submit"
          @submit.prevent="submit"
          class="pa-4"
        >
          <div class="d-flex">
            <v-textarea
              id="acq-input"
              v-if="!select_id && show_acquisitions"
              label="Acquisition ID(s)"
              v-model="model.acquisitions"
              hint="Acquisitions to order"
              :rules="validators.required"
              :placeholder="acq_id_placeholder"
              persistent-placeholder
              outlined
              auto-grow
              rows="6"
              clearable
              :error-messages="invalidAcquisitionsError"
              :loading="pending"
              @click:clear="removeCellExtents()"
            >
              <template v-slot:append>
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      id="cell-extents-button"
                      v-bind="attrs"
                      v-on="on"
                      :disabled="
                        !model.acquisitions ||
                        model.acquisitions.length === 0 ||
                        pending
                      "
                      @click="getCellExtents()"
                      x-small
                      icon
                    >
                      <v-icon> mdi-map-marker-radius </v-icon>
                    </v-btn>
                  </template>
                  <span>Add cell extents of acquisitions to map</span>
                </v-tooltip>
              </template>
            </v-textarea>
            <tooltip-modal v-if="!select_id && show_acquisitions">
              The id(s) of specific acquisitions. May be separated by new lines,
              commas or spaces.
            </tooltip-modal>
          </div>

          <div class="d-flex pb-8">
            <v-switch
              v-model="model.dryRun"
              id="dry-run-switch"
              label="Dry Run"
            ></v-switch>
            <tooltip-modal :class_def="'mx-2 pt-3'">
              Dry run orders will test validations without ordering any imagery
              or incurring any cost
            </tooltip-modal>
          </div>

          <div class="d-flex">
            <v-text-field
              v-if="select_id"
              v-model="select_id"
              filled
              outlined
              class="mt-4"
              label="Select ID"
            />
            <tooltip-modal v-if="select_id">
              Submit an order using the results of a select request by using the
              select id.
            </tooltip-modal>
          </div>

          <v-autocomplete
            hide-details=""
            v-model="selectedPlatform"
            :items="platforms"
            item-text="label"
            item-value="value"
            hide-selected
            dense
            outlined
            :rules="validators.required"
            label="Delivery Platform"
          ></v-autocomplete>

          <span v-if="selectedPlatform == 'amazon_s3'">
            <div class="d-flex mt-6">
              <v-combobox
                id="s3-bucket-input"
                ref="s3_bucket"
                @update:search-input="updateS3Bucket"
                :items="this.s3_buckets"
                append-icon=""
                hide-no-data
                outlined
                dense
                filled
                hint="Required"
                persistent-hint
                type="text"
                label="S3 Bucket"
                :rules="validators.required"
              >
                <template v-slot:append>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        ref="s3_bucket_policy_btn"
                        id="bucket-policy-button"
                        v-bind="attrs"
                        v-on="on"
                        :disabled="!amazon_s3.bucket"
                        @click="policyBtnClick($event)"
                        x-small
                        icon
                      >
                        <v-icon>{{
                          showBucketPolicy
                            ? "mdi-file-remove-outline"
                            : "mdi-file-eye-outline"
                        }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{
                      showBucketPolicy
                        ? "Hide bucket policy"
                        : "Show bucket policy"
                    }}</span>
                  </v-tooltip>
                </template>
              </v-combobox>
              <tooltip-modal>
                The destination S3 bucket for order delivery.
              </tooltip-modal>
            </div>

            <v-expand-transition>
              <div class="mb-4" v-if="showBucketPolicy">
                <div class="d-flex align-center justify-space-between">
                  <div class="text-caption text--secondary">Bucket Policy</div>
                  <div>
                    <v-tooltip right>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          @click.prevent="
                            () => {
                              showBucketPolicy = false;
                            }
                          "
                          x-small
                          icon
                        >
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </template>
                      <span>Hide bucket policy</span>
                    </v-tooltip>
                  </div>
                </div>
                <div style="position: relative">
                  <pre id="s3-bucket-policy" class="s3-bucket-policy">
{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Sid": "MaxarARDS3Access",
            "Effect": "Allow",
            "Principal": {
                "AWS": "arn:aws:iam::{{ s3BucketAccountId }}:root"
            },
            "Action": "s3:PutObject",
            "Resource": [
                "arn:aws:s3:::{{ amazon_s3.bucket }}",
                "arn:aws:s3:::{{ amazon_s3.bucket }}/*"
            ]
        }
    ]
}</pre
                  >
                  <v-btn
                    outlined
                    :disabled="!amazon_s3.bucket"
                    style="position: absolute; bottom: 0; right: 0"
                    tile
                    @click.prevent="copyPolicy()"
                    >Copy</v-btn
                  >
                </div>
              </div>
            </v-expand-transition>

            <div class="d-flex mt-4">
              <v-text-field
                id="s3-prefix-input"
                ref="s3_prefix"
                v-model="amazon_s3.prefix"
                outlined
                dense
                filled
                hint="Required"
                persistent-hint
                type="text"
                label="S3 Prefix"
                autocomplete="off"
                :rules="validators.required"
              ></v-text-field>
              <tooltip-modal>
                The prefix destination for delivery.
              </tooltip-modal>
            </div>
          </span>
          <span v-if="selectedPlatform == 'azure_blob_storage'">
            <v-radio-group v-model="credMethod" mandatory>
              <template v-slot:label>
                <div>Select which credentials method to be used</div>
              </template>
              <v-radio
                label="Registered Credentials ID"
                value="cred_id"
                v-on:change="azure_blob_storage.sas_url = null"
              ></v-radio>
              <v-radio
                label="SAS URL"
                value="manual"
                v-on:change="azure_blob_storage.credentials_id = null"
              ></v-radio>
            </v-radio-group>
            <div class="d-flex">
              <v-text-field
                v-if="credMethod === 'cred_id'"
                v-model="azure_blob_storage.credentials_id"
                filled
                outlined
                dense
                :rules="validators.required"
                class="mt-4"
                label="Registered Credentials ID"
                hint="Required"
                persistent-hint
              />
              <tooltip-modal v-if="credMethod === 'cred_id'">
                Preregistered Azure credentials with access to your delivery
                location
              </tooltip-modal>
            </div>

            <div class="d-flex">
              <v-text-field
                v-if="credMethod === 'manual'"
                v-model="azure_blob_storage.sas_url"
                filled
                outlined
                dense
                :rules="validators.required"
                class="mt-4"
                label="SAS URL"
                hint="Required"
                persistent-hint
              />
              <tooltip-modal v-if="credMethod === 'manual'">
                Manually enter Azure credentials with access to your delivery
                location
              </tooltip-modal>
            </div>

            <div class="d-flex">
              <v-combobox
                ref="azure_container"
                @update:search-input="updateAzureContainer"
                :items="this.azure_containers"
                append-icon=""
                hide-no-data
                outlined
                filled
                dense
                hint="Required"
                persistent-hint
                type="text"
                label="Blob Storage Container"
                :rules="validators.required"
              ></v-combobox>
              <tooltip-modal>
                The destination storage container for order delivery.
              </tooltip-modal>
            </div>

            <div class="d-flex">
              <v-text-field
                ref="azure_prefix"
                v-model="azure_blob_storage.prefix"
                outlined
                filled
                dense
                hint="Required"
                persistent-hint
                type="text"
                label="Blob Storage Prefix"
                autocomplete="off"
                :rules="validators.required"
              ></v-text-field>
              <tooltip-modal>
                The prefix destination for delivery.
              </tooltip-modal>
            </div>
          </span>
          <span v-if="selectedPlatform == 'google_cloud_storage'">
            <v-radio-group v-model="credMethod" mandatory>
              <template v-slot:label>
                <div>Select which credentials method to be used</div>
              </template>
              <v-radio
                label="Registered Credentials ID"
                value="cred_id"
                v-on:change="google_cloud_storage.service_credentials = null"
              ></v-radio>
              <v-radio
                label="Google Cloud Service Credentials"
                value="manual"
                v-on:change="google_cloud_storage.credentials_id = null"
              ></v-radio>
            </v-radio-group>
            <div class="d-flex">
              <v-text-field
                v-if="credMethod === 'cred_id'"
                v-model="google_cloud_storage.credentials_id"
                filled
                outlined
                dense
                :rules="validators.required"
                class="mt-4"
                label="Registered Credentials ID"
                hint="Required"
                persistent-hint
              />
              <tooltip-modal v-if="credMethod === 'cred_id'">
                Preregistered GCS credentials with access to your delivery
                location.
              </tooltip-modal>
            </div>

            <div class="d-flex">
              <v-text-field
                v-if="credMethod === 'manual'"
                v-model="google_cloud_storage.service_credentials"
                filled
                outlined
                dense
                :rules="validators.required"
                class="mt-4"
                label="base64-encoded credentials"
                hint="Required"
                persistent-hint
              />
              <tooltip-modal v-if="credMethod === 'manual'">
                Manually enter GCS credentials with access to your delivery
                location. GCS credentials must be base64-encoded.
              </tooltip-modal>
            </div>

            <div class="d-flex">
              <v-combobox
                ref="gcs_bucket"
                @update:search-input="updateGCSBucket"
                :items="this.gcs_buckets"
                append-icon=""
                hide-no-data
                outlined
                filled
                dense
                hint="Required"
                persistent-hint
                type="text"
                label="Google Cloud Bucket"
                :rules="validators.required"
              ></v-combobox>
              <tooltip-modal>
                The destination GCS bucket for order delivery.
              </tooltip-modal>
            </div>

            <div class="d-flex">
              <v-combobox
                ref="gcs_prefix"
                v-model="google_cloud_storage.prefix"
                outlined
                filled
                dense
                hint="Required"
                persistent-hint
                type="text"
                label="Google Cloud Prefix"
                autocomplete="off"
                :rules="validators.required"
                append-icon=""
              ></v-combobox>
              <tooltip-modal>
                The prefix destination for delivery.
              </tooltip-modal>
            </div>
          </span>

          <div class="d-flex">
            <v-combobox
              id="email-input"
              ref="email"
              @update:search-input="updateEmail"
              :items="this.emails"
              append-icon=""
              hide-no-data
              outlined
              filled
              dense
              autocomplete="nope"
              type="text"
              label="Email Address"
              :rules="[
                (v) =>
                  !v ||
                  /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
                  'E-mail must be valid',
              ]"
            ></v-combobox>
            <tooltip-modal>
              The email address to send order notifications to.
            </tooltip-modal>
          </div>

          <div class="d-flex">
            <v-combobox
              id="sns-input"
              ref="sns-topic-arn"
              @update:search-input="updateSNS"
              :items="this.snsTopics"
              append-icon=""
              hide-no-data
              outlined
              filled
              dense
              autocomplete="nope"
              type="text"
              label="SNS Topic ARN"
              :rules="[
                (v) =>
                  !v ||
                  /^(arn:(?:aws|aws-cn|aws-us-gov):[\w\d-]+:[\w\d-]*:\d{0,12}:[\w\d-]*\/?[\w\d-]*)(\/.*)?.*$/.test(v) ||
                  'SNS Topic ARN must be valid',
              ]"
            ></v-combobox>
            <tooltip-modal>
              We will send a SNS (Simple Notification Service) to the Topic
              ARN (Amazon Resource Name) specified. The following are general
              formats for ARNs:<br /><br />
              arn:partition:service:region:account-id:resource-id<br />
              arn:partition:service:region:account-id:resource-type/resource-id<br />
              arn:partition:service:region:account-id:resource-type:resource-id<br />
            </tooltip-modal>
          </div>

          <v-list-group :value="false">
            <template v-slot:activator>
              <v-list-item-title>Order Settings</v-list-item-title>
            </template>
            <v-list-item>
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.bundle_adjust"
                  label="Bundle Adjust"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Run the Bundle Block Adjustment (BBA) process by activating
                  this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item v-if="is_internal">
              <div class="d-flex">
                <v-switch v-model="model.settings.p3dr" label="P3DR"></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Run the Precision 3D Registration (P3DR) process by activating
                  this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item>
              <div class="d-flex">
                <v-switch v-model="model.settings.hd" label="HD"></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Run the High Definition (HD) process by activating this
                  switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item v-if="is_internal">
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.visual"
                  label="Visual Tile"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver visual RGB image file by activating this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item v-if="is_internal">
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.ms_analytic"
                  label="Multispectral Tile"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver multispectral 4 or 8 band image file by activating
                  this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item v-if="is_internal">
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.pan_analytic"
                  label="Panchromatic Tile"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver single band panchromatic image file by activating this
                  this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item>
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.cloud_mask"
                  label="Cloud Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver cloud and cloud shadows mask files by activating
                  switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item v-if="is_internal">
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.data_mask"
                  label="Data Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver data boundary mask files by activating this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item>
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.healthy_vegetation_mask"
                  label="Healthy Vegetation Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver NDVI derived healthy vegetation mask files by
                  activating this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item>
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.ms_saturation_mask"
                  label="Multispectral Saturation Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver high reflective multispectral mask files by activating
                  this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item>
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.pan_flare_mask"
                  label="Panchromatic Flare Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver high reflective panchromatic mask files by activating
                  this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item>
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.terrain_shadow_mask"
                  label="Terrain Shadow Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver terrain shadow mask files by activating this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item>
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.water_mask"
                  label="Water Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver water mask files by activating this switch.
                </tooltip-modal>
              </div>
            </v-list-item>

            <v-list-item v-if="is_internal">
              <div class="d-flex">
                <v-switch
                  v-model="model.settings.building_mask"
                  label="Building Mask"
                ></v-switch>
                <tooltip-modal :class_def="'mx-2 pt-3'">
                  Deliver building mask files by activating this switch.
                </tooltip-modal>
              </div>
            </v-list-item>
          </v-list-group>

          <v-list-group :value="false">
            <template v-slot:activator>
              <v-list-item-title>
                Order Metadata
                <tooltip-modal :class_def="'mx-2'">
                  Add metadata properties to the order for tracking purposes.
                </tooltip-modal>
              </v-list-item-title>
            </template>

            <div
              v-for="(prop, i) in orderMetadata"
              :key="i"
              class="text-fields-row"
            >
              <v-layout>
                <v-col cols="5">
                  <v-text-field
                    v-model="prop.label"
                    outlined
                    dense
                    filled
                    type="text"
                    label="Label"
                    autocomplete="off"
                  ></v-text-field>
                </v-col>

                <v-col cols="5">
                  <v-text-field
                    v-model="prop.value"
                    outlined
                    dense
                    filled
                    type="text"
                    label="Value"
                    autocomplete="off"
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        class="mr-2"
                        icon
                        x-small
                        @click="removeProp(i)"
                        v-show="i > 0"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>mdi-minus-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Remove metadata property</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="primary"
                        @click="addProp()"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Add another metadata property</span>
                  </v-tooltip>
                </v-col>
              </v-layout>
            </div>
          </v-list-group>
        </v-form>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import validators from "@/utils/validators";
import adminApi from "@/api/admin";
import TooltipModal from "@/components/shared/TooltipModal";
import turfBbox from "@turf/bbox";
import axios from "axios";
import auth from "@/app/auth";
import _ from "lodash";
import { mapGetters } from "vuex";
import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");

const uninterceptedClient = axios.create({
  headers: {
    Authorization: auth.auth.getSignInUserSession().getAccessToken().jwtToken,
  },
});
const request_url = `https://${process.env.VUE_APP_ARD_DOMAIN}/metadata/cells/acquisitions?format=geojson`;

export default {
  mixins: [validators],
  props: {
    defaultModel: {
      type: Object,
      default: () => {
        return {
          dryRun: false,
          acquisitions: [],
          settings: {
            bundle_adjust: false,
            p3dr: false,
            hd: false,
            visual: true,
            ms_analytic: true,
            pan_analytic: true,
            cloud_mask: true,
            data_mask: true,
            healthy_vegetation_mask: true,
            ms_saturation_mask: true,
            pan_flare_mask: true,
            terrain_shadow_mask: true,
            water_mask: true,
            building_mask: false,
          },
        };
      },
    },
    select_id: String,
    show_acquisitions: {
      type: Boolean,
      default: true,
    },
    map: {
      type: Object,
      required: false,
    },
    map_loaded: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    TooltipModal,
  },
  data() {
    return {
      credMethod: null,
      amazon_s3: {
        bucket: null,
        prefix: null,
      },
      azure_blob_storage: {
        credentials_id: null,
        sas_url: null,
        container: null,
        prefix: null,
      },
      google_cloud_storage: {
        credentials_id: null,
        service_credentials: null,
        bucket: null,
        prefix: null,
      },
      emailNotification: {
          type: "email",
          address: null,
      },
      snsNotification: {
          type: "sns",
          topic_arn: null,
      },
      model: {
        ...this.defaultModel,
        settings: { ...this.defaultModel.settings },
      },
      selectedPlatform: "amazon_s3",
      platforms: [
        { label: "Amazon S3", value: "amazon_s3" },
        { label: "Azure Blob Storage", value: "azure_blob_storage" },
        { label: "Google Cloud Storage", value: "google_cloud_storage" },
      ],
      acq_id_placeholder: `ex. 10300100BF6FE700\n57e8689e-0474-43a1-8b3c-bca6b63f6428-inv\n105001002931C500`,
      orders: [],
      s3_buckets: [],
      s3_prefixes: [],
      gcs_buckets: [],
      gcs_prefixes: [],
      azure_containers: [],
      azure_prefixes: [],
      emails: [],
      snsTopics: [],
      pending: false,
      cell_extents: null,
      response: null,
      invalidAcquisitionsError: "",
      cellExtentsSourceName: "Cell Extents",
      orderMetadata: [{}],
      showBucketPolicy: null,
    };
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  mounted() {
    this.$emit("mounted");
    this.build_autofills();
  },
  beforeDestroy() {
    this.removeCellExtents();
    this.map.removeSourceLayer(
      this.cellExtentsSourceName,
      `${this.cellExtentsSourceName} Outline`
    );
  },
  computed: {
    ...mapGetters(["account_details"]),

    is_internal() {
      if (this.account_details) {
        return this.account_details.internal;
      }
      return false;
    },

    s3BucketAccountId() {
      return process.env.VUE_APP_S3_BUCKET_ACCOUNT_ID;
    },
  },
  watch: {
    selectedPlatform: function (new_platform, old_platform) {
      if (old_platform === "amazon_s3") {
        this.$refs.s3_bucket.reset();
        this.$refs.s3_prefix.reset();
      } else if (old_platform == "azure_blob_storage") {
        this.$refs.azure_container.reset();
        this.$refs.azure_prefix.reset();
      } else {
        this.$refs.gcs_bucket.reset();
        this.$refs.gcs_prefix.reset();
      }
    },
    model: {
      deep: true,
      handler(newVal, oldVal) {
        // console.log(`model change=${JSON.stringify(newVal)}`);
        setTimeout(
          (() => {
            // find all invalid inputs using this method
            // since the form.value field that supposedly
            // states if the form is valid doesn't seem to
            // work with more complex rules like the
            // startDateRules and endDateRules within this
            // component
            const invalidInputs = this.$refs.form.inputs.filter(
              (input) => !input.valid
            );

            this.$emit("change", newVal, {
              // valid: this.$refs.form.value, // form.value signifies form validity, commented out since it doesn't work with the startDateRules/endDateRules
              valid: invalidInputs.length === 0,
              dirty:
                JSON.stringify(newVal) !== JSON.stringify(this.defaultModel),
            });
          }).bind(this),
          50
        );
      },
    },
  },
  methods: {
    reset() {
      this.$refs.form.reset();
      this.selectedPlatform = "amazon_s3";
      this.orderMetadata = [{}];

      this.removeCellExtents();
      /* need to give time for form to reset then reset values back to defaults */
      this.$nextTick(() => {
        this.model = { ...this.defaultModel };
      });
    },
    orderParamsValid() {
      if (this.selectedPlatform == "amazon_s3") {
        if (this.amazon_s3.bucket && this.amazon_s3.prefix) {
          return true;
        } else {
          return false;
        }
      } else if (this.selectedPlatform == "azure_blob_storage") {
        if (
          this.azure_blob_storage.container &&
          this.azure_blob_storage.prefix
        ) {
          if (
            this.credMethod == "cred_id" &&
            this.azure_blob_storage.credentials_id
          ) {
            return true;
          } else if (
            this.credMethod == "manual" &&
            this.azure_blob_storage.sas_url
          ) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else if (this.selectedPlatform == "google_cloud_storage") {
        if (
          this.google_cloud_storage.bucket &&
          this.google_cloud_storage.prefix
        ) {
          if (
            this.credMethod == "cred_id" &&
            this.google_cloud_storage.credentials_id
          ) {
            return true;
          } else if (
            this.credMethod == "manual" &&
            this.google_cloud_storage.service_credentials
          ) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    },

    formatAcquisitions(acquisitions) {
      let formatted_acquisitions_ids = [];
      let ids = [];
      if (typeof acquisitions === "string") {
        if (acquisitions.indexOf(",") > -1) {
          ids = acquisitions.split(",");
        } else if (acquisitions.indexOf("\n") > -1) {
          ids = acquisitions.split("\n");
        } else if (acquisitions.indexOf(" ") > -1) {
          ids = acquisitions.split(" ");
        } else {
          //single word
          ids.push(acquisitions);
        }
      }
      ids.forEach((id) => {
        id = id.trim();
        if (id != "") {
          formatted_acquisitions_ids.push(id);
        }
      });
      return formatted_acquisitions_ids;
    },

    build_autofills() {
      const sort = "desc";

      return adminApi
        .get_order_listing_page(null, "", "", "", sort, [])
        .then((response) => {
          response.data.data.reverse().forEach((order) => {
            if ("amazon_s3" in order.output_config) {
              this.s3_buckets.push(order.output_config.amazon_s3.bucket);
              this.s3_prefixes.push(order.output_config.amazon_s3.prefix);
            } else if ("google_cloud_storage" in order.output_config) {
              this.gcs_buckets.push(
                order.output_config.google_cloud_storage.bucket
              );
              this.gcs_prefixes.push(
                order.output_config.google_cloud_storage.prefix
              );
            } else if ("azure_blob_storage" in order.output_config) {
              this.azure_containers.push(
                order.output_config.azure_blob_storage.container
              );
              this.azure_prefixes.push(
                order.output_config.azure_blob_storage.prefix
              );
            }
            if (
              order.notifications &&
              order.notifications.length > 0
            ) {
              order.notifications.forEach((notification) => {
                if (notification.type === "email") {
                  this.emails.push(notification.address);
                }
                if (notification.type === "sns") {
                  this.snsTopics.push(notification.topic_arn);
                }
              })
            }
          });
        })
        .catch(() => {
          // do nothing
        });
    },

    async addToMap() {
      this.map
        .getSource(this.cellExtentsSourceName)
        .setData(this.response.data);

      const newBounds = turfBbox(this.response.data);
      this.map.fitBounds(newBounds, { padding: 200 });
    },

    async getCellExtents() {
      this.pending = true;
      let params = {
        ids: this.formatAcquisitions(this.model.acquisitions),
      };

      uninterceptedClient
        .post(request_url, params)
        .then((response) => {
          this.response = response;
          if (this.response && this.response.status == 200) {
            this.addToMap();
            this.pending = false;
          }
        })
        .catch(() => {
          this.invalidAcquisitionsError =
            "One or more acquisition ID's are invalid";
          this.pending = false;
        });
    },

    removeCellExtents() {
      this.invalidAcquisitionsError = "";
      // null out data
      this.map
        .getSource(this.cellExtentsSourceName)
        .setData({ type: "FeatureCollection", features: [] });
    },

    updateS3Bucket(currentValue) {
      this.amazon_s3.bucket = currentValue;
    },
    updateS3Prefix(currentValue) {
      this.amazon_s3.prefix = currentValue;
    },
    updateAzureContainer(currentValue) {
      this.azure_blob_storage.container = currentValue;
    },
    updateAzurePrefix(currentValue) {
      this.azure_blob_storage.prefix = currentValue;
    },
    updateGCSBucket(currentValue) {
      this.google_cloud_storage.bucket = currentValue;
    },
    updateGCSPrefix(currentValue) {
      this.google_cloud_storage.prefix = currentValue;
    },
    updateEmail(currentValue) {
      this.emailNotification.address = currentValue;
    },
    updateSNS(currentValue) {
      this.snsNotification.topic_arn = currentValue;
    },

    addProp() {
      this.orderMetadata.push({});
    },

    removeProp(index) {
      if (index > 0) {
        this.orderMetadata.splice(index, 1);
      }
    },

    formatOrderMetadata() {
      let metadata = {};
      this.orderMetadata.forEach((property) => {
        if (
          !_.isEmpty(property) &&
          property.label.trim() &&
          property.value.trim()
        ) {
          try {
            metadata[property["label"]] = JSON.parse(property.value);
          } catch {
            metadata[property["label"]] = property.value;
          }
        }
      });

      return metadata;
    },

    clearSelection() {
      if (window.getSelection) {
        if (window.getSelection().empty) {
          // Chrome
          window.getSelection().empty(); // clear current selection
        } else if (window.getSelection().removeAllRanges) {
          // Firefox
          window.getSelection().removeAllRanges(); // clear current selection
        }
      } else if (document.selection) {
        // IE?
        document.selection.empty(); // clear current selection
      }
    },
    copyPolicy() {
      try {
        const containerId = "s3-bucket-policy";
        let range;

        this.clearSelection();
        if (document.selection) {
          range = document.body.createTextRange();
          range.moveToElementText(document.getElementById(containerId));
          range.select().createTextRange();
          document.execCommand("copy");
          document.selection.empty(); // clear current selection
        } else if (window.getSelection) {
          range = document.createRange();
          range.selectNode(document.getElementById(containerId));
          window.getSelection().addRange(range);
          document.execCommand("copy");
        }
        this.clearSelection();

        this.SHOW_SNACKBAR({
          text: `Successfully copied bucket policy text to the clipboard`,
          color: "success",
          icon: "mdi-check",
        });
      } catch (err) {
        this.SHOW_SNACKBAR({
          text: `Copy failed: ${err}`,
          color: "error",
          icon: "mdi-alert-circle-outline",
        });
      }
    },
    policyBtnClick(event) {
      event.stopImmediatePropagation();
      this.showBucketPolicy = !this.showBucketPolicy;
      this.$refs.s3_bucket.blur();
      this.$refs.s3_bucket_policy_btn.$el.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
/* changes the icon of a datetime calendar from black to white */
.theme--dark ::-webkit-calendar-picker-indicator {
  filter: invert(1);
}

.s3-bucket-policy {
  background-color: #1d1c1d;
  border: #4c4b48 solid 1px;
  padding: 0 1rem;
  font-size: 8pt;
}
</style>
